import { ColDef, GridApi, ValueGetterParams } from 'ag-grid-community';
import { BudgetBys } from 'business/constants';
import { TFunction } from 'i18next';
import {
    GESTIONNAIRE_TABLE,
    ORG_CHART_FINANCE_TABLE,
} from 'routes/BudgetModule/config/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { projectionVentilationFormatter } from 'ui/AgGridReact/formatters';
import { phaseComparator } from 'routes/BudgetModule/shared/phases/helpers';

/**
 * Check if it is necessary to either programmatically change the page of the grid or
 * update the url to match the current page of the grid.
 * @param urlPage - The value of '?page' in the url, can be an empty string.
 * @param gridApi - The grid api.
 */
export const checkIfShouldChangePage = (
    urlPage: number,
    gridApi: GridApi,
): boolean => {
    const currentPageIndex = gridApi.paginationGetCurrentPage();
    const totalPages = gridApi.paginationGetTotalPages();
    const urlPageIndex = urlPage - 1;
    const isGridMatchingUrl = urlPageIndex === currentPageIndex;
    const isDataLoaded = totalPages > 0;
    return !isGridMatchingUrl && isDataLoaded;
};

/**
 * Get the numeric value of the '?page' query parameter in the url.
 * Returns 1 by default.
 * @param search a query string, usually from the location object.
 */
export const getPageNumberFromUrl = (search: LocationT['search']): number => {
    const urlPage = new URLSearchParams(search).get('page') ?? '1';
    return parseInt(urlPage, 10);
};

/**
 * Update AgGrid's page if the url's page is different from the grid's page.
 * @param search a query string, usually from the location object.
 * @param gridApi the grid api.
 */
export const updateGridPageIfNecessary = (
    search: LocationT['search'],
    gridApi: GridApi,
): void => {
    const urlPageNumber = getPageNumberFromUrl(search);
    const shouldChangePage = checkIfShouldChangePage(urlPageNumber, gridApi);
    if (shouldChangePage) {
        gridApi.paginationGoToPage(urlPageNumber - 1);
    }
};
export const getBudgetProposalBy = (budgetProposal: BudgetProposalT): string =>
    budgetProposal.by === BudgetBys.GESTIONNAIRE
        ? GESTIONNAIRE_TABLE
        : ORG_CHART_FINANCE_TABLE;

export const getManageBudgetColDefs = (t: TFunction): ColDef[] => {
    const columns: ColDef[] = [
        {
            field: 'exercice',
            headerName: t('common.year'),
            sortable: true,
            sort: 'desc',
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            floatingFilter: true,
        },
        {
            field: 'name',
            headerName: t('common.name'),
            sort: 'asc',
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            floatingFilter: true,
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
            },
        },
        {
            field: 'phase',
            headerName: t('common.phase'),
            cellRenderer: 'phaseOverviewCellRenderer',
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            floatingFilter: true,
            comparator: phaseComparator,
        },
        {
            field: '',
            headerName: t('budget.canResourceEdit'),
            cellRenderer: 'actionCellRenderer',
            valueGetter: (params: ValueGetterParams<BudgetProposalT>) =>
                params.data?.canResourceEdit,
            minWidth: 350,
        },
        {
            field: 'projectionBy',
            headerName: t('budget.projectionBy'),
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            cellRenderer: 'projectionVentilationCellRenderer',
            minWidth: 300,
            valueFormatter: (params) =>
                projectionVentilationFormatter(
                    params.value,
                    params.context.softwares,
                ),
            filterValueGetter: ({ data: line }: { data: BudgetProposalT }) => {
                if (!line) return '';
                return line.projectionBy;
            },
        },
        {
            field: 'pluriannualProjectionBy',
            headerName: t('budget.pluriannualProjectionBy'),
            sortable: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            cellRenderer: 'pluriannualProjectionVentilationCellRenderer',
            minWidth: 300,
            valueFormatter: (params) =>
                projectionVentilationFormatter(
                    params.value,
                    params.context.softwares,
                ),
            filterValueGetter: ({ data: line }: { data: BudgetProposalT }) => {
                if (!line) return '';
                return line.pluriannualProjectionBy;
            },
        },
    ];

    return columns.map((column) => {
        const cols = {
            resizable: true,
            floatingFilter: true,
            suppressFloatingFilterButton: true,
            menuTabs: [
                'filterMenuTab' as const,
                'generalMenuTab' as const,
                'columnsMenuTab' as const,
            ],
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
            },
            ...column,
        };

        return { field: column.field, ...cols };
    });
};
